import { useEffect, useState } from "react";
import { BOOT_STAGES, type KernelStatus } from "@/kernel/Kernel";
import type { SaveState } from "./useAutosave";

const hhmm = (d: Date) => d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });

export function kernelLabel(s: KernelStatus): { text: string; tone: string } {
  switch (s.state) {
    case "ready":
      return { text: "готова", tone: "var(--ok)" };
    case "busy":
      return { text: "выполняется", tone: "var(--warn)" };
    case "booting":
      return { text: `загружается · ${BOOT_STAGES.find((b) => b.stage === s.stage)?.text.toLowerCase() ?? "среда"}`, tone: "var(--warn)" };
    case "dead":
      return { text: "упало", tone: "var(--err)" };
    default:
      return { text: "не запущена", tone: "var(--mut)" };
  }
}

function useMinutes(since?: number) {
  const [, tick] = useState(0);
  useEffect(() => {
    const iv = window.setInterval(() => tick((n) => n + 1), 30_000);
    return () => window.clearInterval(iv);
  }, []);
  return since ? Math.max(0, Math.floor((Date.now() - since) / 60_000)) : 0;
}

export function saveLabel(s: SaveState): string {
  switch (s.kind) {
    case "pending":
    case "saving":
      return "сохраняем…";
    case "offline":
      return "офлайн: правки ждут связи";
    case "error":
      return "не сохранилось";
    default:
      return "автосохранение вкл";
  }
}

/** Панель среды S5 (умбрелла §7.1): «CPU · в браузере», состояние ядра, аптайм воркера. */
export function EnvPanel({ status, lastRun, save }: { status: KernelStatus; lastRun: Date | null; save: SaveState }) {
  const k = kernelLabel(status);
  const minutes = useMinutes(status.startedAt);
  return (
    <div className="flex flex-wrap items-center gap-2.5" data-testid="env-panel">
      <span className="inline-flex items-center gap-2 rounded-md border border-bd bg-card px-2.5 py-1.5 font-mono text-[11.5px] text-ink">
        <span aria-hidden className="size-2 rounded-full" style={{ background: k.tone }} />
        CPU · в браузере
        <span className="text-ink2" data-testid="kernel-state">
          {k.text}
        </span>
      </span>
      <span className="rounded-md border border-dashed border-bd2 px-2.5 py-1.5 font-mono text-[11px] text-ink2" title="GPU появится в следующей версии; пока — «открыть в Colab»">
        GPU · позже
      </span>
      <span className="font-mono text-[11px] text-mut">
        сессия {minutes} мин{lastRun ? ` · последний запуск ${hhmm(lastRun)}` : ""} · <span data-testid="save-state">{saveLabel(save)}</span>
      </span>
    </div>
  );
}

/** Стадии загрузки ядра (E1, под браузер). */
export function BootCard({ status }: { status: KernelStatus }) {
  const idx = BOOT_STAGES.findIndex((b) => b.stage === status.stage);
  return (
    <div className="flex flex-col gap-2 rounded-md border border-bd bg-card px-4 py-3.5" data-testid="boot-card">
      {BOOT_STAGES.map((b, i) => {
        const done = status.state === "ready" || i < idx;
        const active = status.state === "booting" && i === idx;
        return (
          <div key={b.stage} className="grid grid-cols-[18px_minmax(0,1fr)] items-center gap-2.5">
            <span className="font-mono text-[12px]" style={{ color: done ? "var(--ok)" : active ? "var(--warn)" : "var(--mut)" }}>
              {done ? "✓" : active ? "▸" : "·"}
            </span>
            <span className={`text-[13px] ${active ? "text-ink" : "text-ink2"}`}>{b.text}</span>
          </div>
        );
      })}
      <p className="m-0 text-[12px] text-ink2">Первый запуск занимает до минуты — пакеты скачиваются один раз и кэшируются браузером.</p>
    </div>
  );
}
